import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../lib/supabase'
import { useSession } from '../lib/session'
import { Shell } from '../components/Shell'
import { Field, Sill, Spinner, Check } from '../components/ui'

export default function Join() {
  const { user, memberships, approved, refresh } = useSession()
  const nav = useNavigate()
  const [churches, setChurches] = useState(null)
  const [pick, setPick] = useState('')
  const [churchEmail, setChurchEmail] = useState('')
  const [busy, setBusy] = useState(false)
  // null until we've asked. false means the church has no approved staff to
  // reach, or the send failed — either way nobody there knows yet.
  const [notified, setNotified] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    supabase.from('church_directory').select('*').order('name')
      .then(({ data }) => setChurches(data ?? []))
  }, [])

  useEffect(() => { if (approved.length) nav('/') }, [approved, nav])

  const pending = memberships.find((m) => m.membership_status === 'pending')
  const rejected = memberships.find((m) => m.membership_status === 'rejected')

  const request = async () => {
    setBusy(true); setErr(null)
    const { error } = await supabase.from('church_members').insert({
      user_id: user.id,
      church_id: pick,
      role: 'songwriter',
      membership_status: 'pending',
    })
    if (error) { setBusy(false); return setErr(readableError(error)) }

    // Always tell the church. send-church-notice resolves the recipients itself
    // — every approved admin and reviewer, via the service role — so the email
    // address below is optional context for the body, never the trigger. This
    // used to be wrapped in `if (churchEmail.trim())`, which meant a writer
    // without a church-domain address asked to join and nobody ever heard.
    const { data: notice, error: noticeErr } = await supabase.functions
      .invoke('send-church-notice', {
        body: {
          church_id: pick,
          ...(churchEmail.trim() ? { church_email: churchEmail.trim().toLowerCase() } : {}),
        },
      })
    // The request itself already succeeded, so a failed notice must not read as
    // a failed join. It changes what the next screen can honestly promise.
    setNotified(noticeErr ? false : (notice?.sent ?? false))
    setBusy(false)
    await refresh()
  }

  if (churches === null) return <Shell><Spinner /></Shell>

  if (pending) {
    const name = churches.find((c) => c.id === pending.church_id)?.name || 'your church'
    return (
      <Shell>
        <div className="done-ic"><Check size={26} w={2.4} /></div>
        <h2 className="q center">Asked to join {name}.</h2>
        {notified === false ? (
          // Honest rather than reassuring: send-church-notice answers
          // { sent: false } when a church has nobody approved to email yet, and
          // telling someone to sit tight for a person who does not exist is how
          // a request goes stale without anyone noticing.
          <p className="sub center">
            We couldn&rsquo;t reach anyone there yet — nobody at {name} has set up their
            Beseter account. If you know someone on the team, it&rsquo;s worth a nudge.
          </p>
        ) : (
          <p className="sub center">
            We&rsquo;ve let them know. Someone there will wave you through — we&rsquo;ll not chase
            them, and you don&rsquo;t have to either. This page will change when they do.
          </p>
        )}
        <p className="small center">
          Nothing is on hold. Keep writing, drafting and co-signing meanwhile — you just
          can't send them a song until they've verified you.
        </p>
        <button className="btn btn-primary" onClick={() => nav('/me')}>Keep writing</button>
        <button className="btn btn-quiet" style={{ marginTop: 8 }} onClick={refresh}>Check again</button>
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="eyebrow">Your church</div>
      <h2 className="q">Where do you write from?</h2>
      <p className="sub">Pick the church you're part of. They approve you, then you can send them songs.</p>
      <Sill />

      {rejected && (
        <p className="small" style={{ marginBottom: 14 }}>
          Your last request wasn't approved. You can ask a different church.
        </p>
      )}

      <Field label="Church">
        <select className="input" value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Choose a church…</option>
          {churches.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}{c.city ? ` · ${c.city}` : ''}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Your church email (optional)">
        <input className="input" type="email" value={churchEmail}
               onChange={(e) => setChurchEmail(e.target.value)} placeholder="you@thatchurch.org" />
      </Field>
      <p className="small" style={{ marginTop: -8, marginBottom: 16 }}>
        We&rsquo;ll let the church know either way. An address at their domain just helps
        them place you faster.
      </p>

      <button className="btn btn-primary" disabled={busy || !pick} onClick={request}>
        {busy ? 'Asking…' : 'Ask to join'}
      </button>
      <button className="btn-plain" style={{ width: '100%', marginTop: 10 }}
              onClick={() => nav('/church/new')}>
        My church isn't here — set it up
      </button>
      <button className="btn-plain" style={{ width: '100%' }} onClick={() => nav('/me')}>
        Not now — I'll just write
      </button>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
